/**
 * Pointage (Attendance Tracking) Types for HR Module
 */

export type PointageStatus =
  | 'PRESENT'
  | 'PRESENT_7H'
  | 'PRESENT_6H'
  | 'PRESENT_5H'
  | 'PRESENT_4H'
  | 'PRESENT_3H'
  | 'PRESENT_2H'
  | 'PRESENT_1H'
  | 'ABSENT'
  | 'WEEKEND'
  | 'FERIE'
  | 'CONGE_PAYE'
  | 'CONGE_MALADIE'
  | 'CONGE_MARIAGE'
  | 'CONGE_DECES'
  | 'CONGE_NAISSANCE';

export interface PointageEntry {
  id: number;
  employee_id: number;
  date: string;
  status: PointageStatus;
  heure_arrivee: string | null;
  heure_depart: string | null;
  heures_travaillees: number | null;
  heures_supplementaires: number | null;
  note: string | null;
}

export interface PointageEntryCreate {
  employee_id: number;
  date: string;
  status: PointageStatus;
  heure_arrivee?: string | null;
  heure_depart?: string | null;
  heures_travaillees?: number | null;
  heures_supplementaires?: number | null;
  note?: string | null;
}

export interface PointageEntryUpdate {
  status?: PointageStatus;
  heure_arrivee?: string | null;
  heure_depart?: string | null;
  heures_travaillees?: number | null;
  heures_supplementaires?: number | null;
  note?: string | null;
}

export interface PointageBulkUpdate {
  employee_id: number;
  date: string;
  status: PointageStatus;
}

export interface EmployeePointageRow {
  employee_id: number;
  matricule: string;
  nom_complet: string;
  statut: string | null;  // Employee status (ACTIF, INACTIF, SUSPENDU)
  entries: Record<string, PointageEntry>; // Key: date string (YYYY-MM-DD)
  // Présences
  total_jours_present: number;
  total_heures_present: number;
  // Absences
  total_absent: number;
  // Congés
  total_conge_paye: number;
  total_conge_maladie: number;
  total_conge_mariage: number;
  total_conge_deces: number;
  total_conge_naissance: number;
  total_jour_ferie: number;
  total_conge: number;  // sum of all congés
  // Totals
  total_heures: number;
  total_heures_sup: number;
}

export interface MonthlyPointageResponse {
  month: number;
  year: number;
  days_in_month: number;
  working_days: number;
  employees: EmployeePointageRow[];
}

export interface PointageStats {
  total_employees: number;
  present_today: number;
  absent_today: number;
  on_leave_today: number;
  total_working_days: number;
  average_presence_rate: number;
}

export interface PointageDaySummary {
  date: string;
  total_present: number;
  total_absent: number;
  total_conge: number;
  total_autorisation: number;
  total_maladie: number;
}

// Status configuration for UI
// Based on 191h/26 days = 7.346154h per day
export const POINTAGE_STATUS_CONFIG: Record<
  PointageStatus,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  PRESENT: {
    label: 'Présent',
    color: '#fff',
    bgColor: '#4CAF50',
    icon: 'P',
  },
  PRESENT_7H: {
    label: 'P-1h ',
    color: '#fff',
    bgColor: '#66BB6A',
    icon: 'P1',
  },
  PRESENT_6H: {
    label: 'P-2h',
    color: '#fff',
    bgColor: '#81C784',
    icon: 'P2',
  },
  PRESENT_5H: {
    label: 'P-3h',
    color: '#fff',
    bgColor: '#A5D6A7',
    icon: 'P3',
  },
  PRESENT_4H: {
    label: 'P-4h',
    color: '#000',
    bgColor: '#C8E6C9',
    icon: 'P4',
  },
  PRESENT_3H: {
    label: 'P-5h',
    color: '#000',
    bgColor: '#DCEDC8',
    icon: 'P5',
  },
  PRESENT_2H: {
    label: 'P-6h',
    color: '#000',
    bgColor: '#F0F4C3',
    icon: 'P6',
  },
  PRESENT_1H: {
    label: 'P-7h',
    color: '#000',
    bgColor: '#FFF9C4',
    icon: 'P7',
  },
  ABSENT: {
    label: 'Absent',
    color: '#fff',
    bgColor: '#F44336',
    icon: 'A',
  },
  WEEKEND: {
    label: 'Weekend',
    color: '#666',
    bgColor: '#E0E0E0',
    icon: '',
  },
  FERIE: {
    label: 'Férié',
    color: '#666',
    bgColor: '#BDBDBD',
    icon: 'F',
  },
  CONGE_PAYE: {
    label: 'Congé Payé',
    color: '#fff',
    bgColor: '#1976D2',
    icon: 'CP',
  },
  CONGE_MALADIE: {
    label: 'Congé Maladie',
    color: '#fff',
    bgColor: '#9C27B0',
    icon: 'CM',
  },
  CONGE_MARIAGE: {
    label: 'Congé Mariage',
    color: '#fff',
    bgColor: '#E91E63',
    icon: 'MA',
  },
  CONGE_DECES: {
    label: 'Congé Décès',
    color: '#fff',
    bgColor: '#424242',
    icon: 'DE',
  },
  CONGE_NAISSANCE: {
    label: 'Congé Naissance',
    color: '#fff',
    bgColor: '#00BCD4',
    icon: 'NA',
  },
};

// Status options for dropdown
// Based on 191h/26 days = 7.346154h per day
export const POINTAGE_STATUS_OPTIONS: { value: PointageStatus; label: string }[] = [
  { value: 'PRESENT', label: 'Présent' },
  { value: 'PRESENT_7H', label: 'P-1h' },
  { value: 'PRESENT_6H', label: 'P-2h' },
  { value: 'PRESENT_5H', label: 'P-3h' },
  { value: 'PRESENT_4H', label: 'P-4h' },
  { value: 'PRESENT_3H', label: 'P-5h' },
  { value: 'PRESENT_2H', label: 'P-6h' },
  { value: 'PRESENT_1H', label: 'P-7h' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'CONGE_PAYE', label: 'Congé Payé' },
  { value: 'CONGE_MALADIE', label: 'Congé Maladie' },
  { value: 'CONGE_MARIAGE', label: 'Congé Mariage' },
  { value: 'CONGE_DECES', label: 'Congé Décès' },
  { value: 'CONGE_NAISSANCE', label: 'Congé Naissance' },
  { value: 'FERIE', label: 'Jour Férié' },
];

// Day names in French
export const DAY_NAMES_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// Month names in French
export const MONTH_NAMES_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];
